import { Printer } from 'lucide-react';
import { printHtml } from '../lib/print.js';
import { escapeHtml } from '../lib/html-escape.js';
import { formatAddressLines } from '../lib/address-snapshot.js';
import type { PackingSlip } from '../types/orders.js';

export interface PackingSlipViewProps {
  packingSlip: PackingSlip;
}

/** Deliberately excludes cost price, payment detail, coupon rules, and any customer account data beyond the recipient/delivery address (Part 20). No shipping-label or carrier integration -- print-only. */
export function PackingSlipView({ packingSlip }: PackingSlipViewProps) {
  function handlePrint() {
    const addressLines = formatAddressLines(packingSlip.deliveryAddress).map(escapeHtml).join('<br/>');
    const itemRows = packingSlip.items
      .map(
        (item) => `<tr>
          <td>${escapeHtml(item.productName)} — ${escapeHtml(item.variantName)}</td>
          <td>${escapeHtml(item.sku)}</td>
          <td>${item.quantity}</td>
        </tr>`,
      )
      .join('');

    printHtml(
      `Packing Slip - ${packingSlip.orderNumber}`,
      `<h1>Packing Slip</h1>
      <p class="muted">Order ${escapeHtml(packingSlip.orderNumber)}</p>
      <h2>Deliver to</h2>
      <p>${addressLines}</p>
      <table>
        <thead><tr><th>Item</th><th>SKU</th><th>Qty</th></tr></thead>
        <tbody>${itemRows}</tbody>
      </table>`,
    );
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-gray-500">Delivery-ready packing slip.</p>
        <button
          type="button"
          onClick={handlePrint}
          className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50"
        >
          <Printer className="h-4 w-4" aria-hidden="true" />
          Print
        </button>
      </div>
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-400">
            <th className="py-2 pr-4 font-medium">Item</th>
            <th className="py-2 pr-4 font-medium">SKU</th>
            <th className="py-2 pr-4 font-medium">Qty</th>
          </tr>
        </thead>
        <tbody>
          {packingSlip.items.map((item, index) => (
            <tr key={index} className="border-b border-gray-50">
              <td className="py-2 pr-4">
                {item.productName} — {item.variantName}
              </td>
              <td className="py-2 pr-4">{item.sku}</td>
              <td className="py-2 pr-4">{item.quantity}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
